import { useEffect } from "react"
import { CustomTimePicker, Divider, ErrorMessage, Required } from "@components"
import { Divider as HeroDivider, Input } from "@heroui/react";
import { Controller, useForm } from "react-hook-form";
import { DayPicker } from 'react-day-picker';
import { PropTypes } from "prop-types"
import 'react-day-picker/dist/style.css';

const EventSessionForm = ({ 
  children, 
  handleSubmit,
  isReadOnly = false,
  onDirtyChange = () => {}, 
  onErrorChange = () => {},
  session = {
    id: null,
    title: "",
    start_date: null,
    start_time: null,
    end_time: null,
    slot_duration: 5,
    slot_gap: 0
  }
}) => {
  const { control, getValues, handleSubmit: handleValidateForm, formState: { errors, isDirty, dirtyFields }, register, trigger } = useForm({
    defaultValues: {
      id: session.id,
      title: session.title,
      start_date: session.start_date,
      start_time: session.start_time,
      end_time: session.end_time,
      slot_duration: session.slot_duration,
      slot_gap: session.slot_gap
    }
  })

  useEffect(() => {
    onErrorChange?.({...errors})
  }, [errors]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onDirtyChange?.(isDirty, {...dirtyFields})
  }, [isDirty, JSON.stringify(dirtyFields)]) // eslint-disable-line react-hooks/exhaustive-deps


  const validateTimes = (values) => {
    if (!values.end_time) {
      return "Select an end time for your session"
    }

    if (values.end_time && values.start_time && values.end_time.isSameOrBefore(values.start_time)) {
      return "End time must be after start time."
    }

    return true
  }

  return (
    <form id="session-form" className="flex flex-col gap-y-4" onSubmit={handleValidateForm((data) => handleSubmit(data))} noValidate>
      <Input
        {...register("title", { required: "You must supply a name for your session", minLength: { value: 3, message: "The session name must be at least 3 characters" } })}
        name="title"
        autoFocus
        isRequired
        type="text"
        label="Session Name"
        placeholder="Enter a title for your session"
        labelPlacement="outside"
        defaultValue={session.title}
        isInvalid={errors.hasOwnProperty("title")}
        errorMessage={errors?.title?.message}
        isDisabled={isReadOnly}
      />

      <Divider />

      <div>
        <h5 className="mb-1 text-base font-semibold">Session Date &amp; Time</h5>
        <p className="mb-3 text-sm text-content2-foreground">Select the session day and start / end times.</p>

        <div className="flex justify-between my-4">
          <Controller
            control={control}
            rules={{ required: "You must select a start date for your session" }}
            name="start_date"
            render={({ field: { value, onChange } }) => (
              <div>
                <DayPicker
                  selected={value}
                  onSelect={onChange}
                  mode="single"
                  fromMonth={new Date()}
                  disabled={isReadOnly ? [(date) => true] : { before: new Date() }}
                />
                {errors.hasOwnProperty("start_date") && <ErrorMessage>{errors?.start_date?.message}</ErrorMessage>}
              </div>
            )}
          />

          <HeroDivider orientation="vertical" className="mx-4" />

          <div>
            <div className="mb-4">
              <p className="mb-1 text-sm">Start Time<Required /></p>

              <Controller
                control={control}
                rules={{ required: "Select a start time for your session" }}
                name="start_time"
                render={({ field: { value, onChange } }) => (
                  <div>
                    <CustomTimePicker showSecond={false} minuteStep={5} allowEmpty={false} onChange={(value) => { onChange(value); trigger('end_time'); }} value={value} disabled={isReadOnly} />
                    {errors.hasOwnProperty("start_time") && <ErrorMessage>{errors?.start_time?.message}</ErrorMessage>}
                  </div>
                )}
              />
            </div>

            <div>
              <p className="mb-1 text-sm">End Time<Required /></p>

              <Controller
                control={control}
                rules={{ validate: () => validateTimes(getValues()) }}
                name="end_time"
                render={({ field: { value, onChange } }) => (
                  <div>
                    <CustomTimePicker showSecond={false} minuteStep={5} allowEmpty={false} onChange={(value) => { onChange(value); trigger('end_time'); }} value={value} disabled={isReadOnly} />
                    {errors.hasOwnProperty("end_time") && <ErrorMessage>{errors?.end_time?.message}</ErrorMessage>}
                  </div>
                )}
              />
            </div>
          </div>
        </div>

        <Divider />
      </div>

      <div>
        <h5 className="mb-1 text-base font-semibold">Slots & Break Durations</h5>
        <p className="mb-5 text-sm text-content2-foreground">Select the slot length and break durations.</p>

        <div className="flex gap-x-4">
          <Input
            {...register("slot_duration", { valueAsNumber: true, required: "You must supply a slot length for your session", min: { value: 3, message: "The slot length must be at least 3 minutes" }, max: { value: 120, message: "The slot length must be less than 120 minutes" } })}
            isRequired
            name="slot_duration"
            type="number"
            min="3"
            label="Slot Length (min 3 mins)"
            placeholder="The length of a slot"
            labelPlacement="outside"
            defaultValue={session.slot_duration}
            isInvalid={errors.hasOwnProperty("slot_duration")}
            errorMessage={errors?.slot_duration?.message}
            isDisabled={isReadOnly}
          />

          <Input
            {...register("slot_gap", { valueAsNumber: true, required: "You must supply a break length for your session", max: { value: 120, message: "The break length must be less than 120 minutes" } })}
            isRequired
            type="number"
            min="0"
            label="Break Length"
            placeholder="The time between slots"
            labelPlacement="outside"
            defaultValue={session.slot_gap}
            isInvalid={errors.hasOwnProperty("slot_gap")}
            errorMessage={errors?.slot_gap?.message}
            isDisabled={isReadOnly}
          />
        </div>
      </div>

      {children}
    </form>
  )
}

EventSessionForm.propTypes = {
  children: PropTypes.node,
  handleSubmit: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  onDirtyChange: PropTypes.func,
  onErrorChange: PropTypes.func,
  session: PropTypes.object,
}

export default EventSessionForm